import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Upload, FileSpreadsheet, Loader2, CheckCircle2,
  UserCheck, ArrowRight, ChevronsUpDown,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { leadsAPI, agentAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { isKRRole } from '@/lib/utils';

// Two-step import flow: (1) upload + preview → (2) map columns and confirm.
// Step 1 asks the server to parse the file and return the headers so we can
// render a mapping UI. Step 2 sends the file back with the finalized mapping
// as a JSON string on the multipart body.

const FIELD_LABELS = {
  first_name: 'First name*',
  last_name: 'Last name',
  gender: 'Gender (F/M)',
  email: 'Email',
  phone: 'Phone',
  address: 'Address',
  state: 'State',
  county: 'County',
  product_of_interest: 'Product of interest',
  notes: 'Notes',
  date_of_birth: 'Date of birth',
  ignore: '— Ignore this column —',
};

export default function LeadsImportPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isKR = isKRRole(user);
  const [step, setStep] = useState('upload'); // upload | map | done
  const [file, setFile] = useState(null);
  const [assignNpn, setAssignNpn] = useState('none');
  const [sourceLabel, setSourceLabel] = useState('');
  const [agents, setAgents] = useState([]);
  const [previewing, setPreviewing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!isKR) return;
    agentAPI.list().then((r) => setAgents(r.data || [])).catch(() => {});
  }, [isKR]);

  const runPreview = async () => {
    if (!file) { toast.error('Pick a file'); return; }
    setPreviewing(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await leadsAPI.importPreview(fd);
      setPreview(res.data);
      setMapping(res.data.auto_mapping || {});
      setStep('map');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const submit = async () => {
    // Validate mapping: at least one column must map to first_name.
    const mapped = Object.values(mapping);
    if (!mapped.includes('first_name')) {
      toast.error('You must map at least one column to "First name" — it is required.');
      return;
    }
    // Duplicates: no two columns can target the same field (except 'ignore').
    const nonIgnore = mapped.filter((v) => v !== 'ignore');
    if (new Set(nonIgnore).size !== nonIgnore.length) {
      toast.error('Two columns are mapped to the same field. Pick a different field for each column, or set one to "Ignore".');
      return;
    }
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (isKR && assignNpn && assignNpn !== 'none') fd.append('assign_agent_npn', assignNpn);
      if (sourceLabel.trim()) fd.append('source_label', sourceLabel.trim());
      fd.append('mapping', JSON.stringify(mapping));
      const res = await leadsAPI.importFile(fd);
      setResult(res.data);
      setStep('done');
      toast.success(`Imported ${res.data.created} lead(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed');
    } finally { setUploading(false); }
  };

  return (
    <div className="page-content max-w-4xl space-y-6" data-testid="leads-import-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/leads')}>
          <ArrowLeft size={16} className="mr-1" /> Back
        </Button>
        <div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900">Import Leads</h1>
          <p className="text-sm text-slate-500">
            CSV or Excel (.xlsx). Two-step flow — upload the file, then map your columns to lead fields.
          </p>
        </div>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-2 text-xs">
        <StepPill active={step === 'upload'} done={step !== 'upload'} label="1. Upload" />
        <ArrowRight size={12} className="text-slate-400" />
        <StepPill active={step === 'map'} done={step === 'done'} label="2. Map columns" />
        <ArrowRight size={12} className="text-slate-400" />
        <StepPill active={step === 'done'} done={false} label="3. Done" />
      </div>

      {step === 'upload' && (
        <Card>
          <CardHeader><CardTitle className="text-base">Upload File</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>File</Label>
              <div className="border-2 border-dashed border-slate-300 rounded-md p-6 text-center">
                <FileSpreadsheet size={28} className="mx-auto text-slate-400 mb-2" />
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  data-testid="import-file-input"
                />
                {file && <p className="text-xs text-slate-500 mt-2">{file.name} — {(file.size / 1024).toFixed(1)} KB</p>}
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Any column layout works — we'll show you the headers on the next step and let you map them
                to lead fields. Only <code className="bg-slate-100 px-1 rounded">first_name</code> is required.
              </p>
            </div>

            <div>
              <Label>Source (optional)</Label>
              <Input
                placeholder="e.g. Community event — Miami, Jan 25 2026"
                value={sourceLabel}
                onChange={(e) => setSourceLabel(e.target.value)}
                data-testid="import-source-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Every lead in this file will be tagged with this source. Handy for tracking events,
                trade shows, referral partners, etc.
              </p>
            </div>

            {isKR ? (
              <div>
                <Label>Assign all imported leads to (optional)</Label>
                <Select value={assignNpn} onValueChange={setAssignNpn}>
                  <SelectTrigger data-testid="import-assign-select"><SelectValue placeholder="Leave unassigned" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Leave unassigned</SelectItem>
                    {agents.map((a) => (
                      <SelectItem key={a.npn} value={a.npn}>
                        {a.name || `${a.first_name || ''} ${a.last_name || ''}`.trim() || a.email} ({a.npn})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                <UserCheck size={16} className="mt-0.5" />
                <div>
                  <p className="font-medium">Leads will be assigned to you</p>
                  <p className="text-xs">
                    Everything in this file is added to your pipeline immediately so you can start
                    following up right away.
                  </p>
                </div>
              </div>
            )}

            <Button onClick={runPreview} disabled={previewing || !file} data-testid="import-preview-btn">
              {previewing ? <Loader2 className="animate-spin mr-2" size={14} /> : <ChevronsUpDown size={14} className="mr-2" />}
              Preview & map columns
            </Button>
          </CardContent>
        </Card>
      )}

      {step === 'map' && preview && (
        <Card data-testid="import-mapping-step">
          <CardHeader>
            <CardTitle className="text-base">
              Map your columns
              <span className="ml-2 text-xs text-slate-500 font-normal">
                ({preview.total_rows} row{preview.total_rows === 1 ? '' : 's'} detected)
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-600">
              For each column in your file, pick the lead field it maps to.
              We auto-detected the mapping below — adjust anything that looks off.
              First name is required.
            </p>

            <div className="border border-slate-200 rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 text-xs">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium w-1/3">Your column</th>
                    <th className="text-left px-3 py-2 font-medium w-1/3">Maps to</th>
                    <th className="text-left px-3 py-2 font-medium">Sample values</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.headers.map((header) => (
                    <tr key={header} className="border-t border-slate-100">
                      <td className="px-3 py-2 font-medium text-slate-800" data-testid={`mapping-header-${header}`}>
                        {header || <span className="italic text-slate-400">(unnamed)</span>}
                      </td>
                      <td className="px-3 py-2">
                        <Select
                          value={mapping[header] || 'ignore'}
                          onValueChange={(v) => setMapping({ ...mapping, [header]: v })}
                        >
                          <SelectTrigger data-testid={`mapping-select-${header}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {preview.allowed_fields.map((f) => (
                              <SelectItem key={f} value={f}>{FIELD_LABELS[f] || f}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-500 space-x-2">
                        {preview.sample_rows.slice(0, 3).map((row, i) => (
                          <span key={i} className="inline-block max-w-[220px] truncate">
                            {row[header] || <span className="text-slate-300">—</span>}
                          </span>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-between pt-2">
              <Button variant="ghost" onClick={() => setStep('upload')} data-testid="mapping-back-btn">
                <ArrowLeft size={14} className="mr-1" /> Back
              </Button>
              <Button onClick={submit} disabled={uploading} data-testid="mapping-confirm-btn">
                {uploading ? <Loader2 className="animate-spin mr-2" size={14} /> : <Upload size={14} className="mr-2" />}
                Import {preview.total_rows} lead{preview.total_rows === 1 ? '' : 's'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 'done' && result && (
        <Card className="border-emerald-200 bg-emerald-50" data-testid="import-result">
          <CardContent className="pt-4 space-y-2 text-sm text-emerald-900">
            <p className="flex items-center gap-2 font-medium">
              <CheckCircle2 size={16} /> Import completed
            </p>
            <p>Created: <span className="font-semibold">{result.created}</span> / {result.total_rows} rows</p>
            {result.skipped > 0 && <p>Skipped: {result.skipped}</p>}
            {result.errors?.length > 0 && (
              <div>
                <p className="font-medium mt-2">First {result.errors.length} issue(s):</p>
                <ul className="text-xs list-disc pl-6 text-emerald-800">
                  {result.errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </div>
            )}
            <div className="flex gap-2 mt-3">
              <Link to="/leads">
                <Button size="sm" variant="outline">View imported leads</Button>
              </Link>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setFile(null); setPreview(null); setMapping({}); setResult(null); setStep('upload');
                }}
                data-testid="import-another-btn"
              >
                Import another file
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function StepPill({ active, done, label }) {
  const cls = active
    ? 'bg-slate-900 text-white'
    : done
    ? 'bg-emerald-100 text-emerald-700 border-emerald-200 border'
    : 'bg-slate-100 text-slate-500 border-slate-200 border';
  return (
    <span className={`px-2 py-1 rounded-full font-medium ${cls}`}>{label}</span>
  );
}
